import React, { useMemo, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { agrogestao } from "@/api/agrogestaoClient"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import ParceiroForm from "@/components/parceiros/ParceiroForm"
import { Plus, Search, User, Truck, Building2, Archive, MapPin, Phone, Mail, MoreHorizontal, Edit, Trash2, FileText } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import Paginacao from "@/components/shared/Paginacao"

const tipos = [
  { value: "cliente", label: "Clientes", icon: User, color: "text-blue-600", bg: "bg-blue-50" },
  { value: "fornecedor", label: "Fornecedores", icon: Building2, color: "text-amber-600", bg: "bg-amber-50" },
  { value: "transportador", label: "Transportadores", icon: Truck, color: "text-indigo-600", bg: "bg-indigo-50" },
  { value: "outro", label: "Outros", icon: Archive, color: "text-stone-600", bg: "bg-stone-50" }
]

export default function Parceiros() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada")
  const [activeTab, setActiveTab] = useState("cliente")
  const [searchTerm, setSearchTerm] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [formTab, setFormTab] = useState("geral")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const queryClient = useQueryClient()

  const { data: parceiros = [], isLoading } = useQuery({
    queryKey: ["parceiros", propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.Parceiro.filter({ propriedade_id: propriedadeId }) : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, currentStatus }) => {
      const newStatus = currentStatus === 'ativo' ? 'inativo' : 'ativo'
      return agrogestao.entities.Parceiro.update(id, { status: newStatus })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["parceiros", propriedadeId] })
  })

  const filtrados = useMemo(() => {
    const base = parceiros.filter(p => (p.tipo || "outro") === activeTab)
    if (!searchTerm) return base
    const s = searchTerm.toLowerCase()
    return base.filter(p =>
      (p.nome_razao || p.razao_social || p.nome || "").toLowerCase().includes(s) ||
      (p.cnpj_cpf || "").toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s)
    )
  }, [parceiros, activeTab, searchTerm])

  const stats = useMemo(() => {
    const total = filtrados.length
    const ativos = filtrados.filter(p => p.status === 'ativo').length
    const inativos = total - ativos
    return { total, ativos, inativos }
  }, [filtrados])

  const handleSuccess = () => {
    setDialogOpen(false)
    setEditing(null)
  }

  const getInitials = (name) => {
    return (name || "").split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase()
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 tracking-tight">Gerenciamento de Parceiros</h1>
          <p className="text-stone-500 text-sm">Cadastre e gerencie seus clientes, fornecedores e parceiros de negócio.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormTab("geral"); setDialogOpen(true) }} className="bg-[#1B4332] hover:bg-[#2D6A4F] shadow-lg shadow-emerald-900/20">
          <Plus className="w-4 h-4 mr-2" /> Novo Cadastro
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-stone-500">Total de Registros</p>
              <h3 className="text-2xl font-bold text-stone-800">{stats.total}</h3>
            </div>
            <div className={`p-3 rounded-full ${tipos.find(t => t.value === activeTab)?.bg}`}>
              {React.createElement(tipos.find(t => t.value === activeTab)?.icon || User, { className: `w-5 h-5 ${tipos.find(t => t.value === activeTab)?.color}` })}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-stone-500">Ativos</p>
              <h3 className="text-2xl font-bold text-emerald-600">{stats.ativos}</h3>
            </div>
            <div className="p-3 rounded-full bg-emerald-50">
              <User className="w-5 h-5 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-stone-500">Inativos</p>
              <h3 className="text-2xl font-bold text-stone-400">{stats.inativos}</h3>
            </div>
            <div className="p-3 rounded-full bg-stone-100">
              <Archive className="w-5 h-5 text-stone-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-stone-100 flex flex-col lg:flex-row gap-4 justify-between items-center bg-stone-50/50">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full lg:w-auto">
            <TabsList className="bg-white border border-stone-200 p-1 h-10 w-full lg:w-auto">
              {tipos.map(t => (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  className="gap-2 data-[state=active]:bg-stone-100 data-[state=active]:text-stone-900"
                >
                  <t.icon className="w-4 h-4" />
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Buscar por nome, documento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {filtrados.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-stone-400" />
              </div>
              <h3 className="text-lg font-medium text-stone-800">Nenhum parceiro encontrado</h3>
              <p className="text-stone-500">Tente mudar o filtro ou adicione um novo cadastro.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/50 hover:bg-stone-50/50">
                  <TableHead className="w-[300px]">Nome / Razão Social</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Localização</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtrados.map((p) => (
                  <TableRow key={p.id} className="group hover:bg-stone-50/50 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 border border-stone-200">
                          <AvatarFallback className="bg-white text-stone-600 font-medium text-xs">
                            {getInitials(p.nome_razao || p.nome)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-stone-800 line-clamp-1">{p.nome_razao || p.razao_social || p.nome}</p>
                          <p className="text-xs text-stone-500 font-mono">{p.cnpj_cpf || 'Sem documento'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {p.telefone && (
                          <div className="flex items-center gap-1.5 text-xs text-stone-600">
                            <Phone className="w-3 h-3 text-stone-400" />
                            {p.telefone}
                          </div>
                        )}
                        {p.email && (
                          <div className="flex items-center gap-1.5 text-xs text-stone-600">
                            <Mail className="w-3 h-3 text-stone-400" />
                            <span className="truncate max-w-[150px]" title={p.email}>{p.email}</span>
                          </div>
                        )}
                        {!p.telefone && !p.email && <span className="text-xs text-stone-400 italic">Sem contato</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm text-stone-600">
                        <MapPin className="w-3.5 h-3.5 text-stone-400" />
                        {p.cidade ? `${p.cidade}${p.estado ? ` - ${p.estado}` : ''}` : <span className="text-stone-400 italic text-xs">Não informado</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.status === 'ativo' ? 'default' : 'secondary'} className={p.status === 'ativo' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 shadow-none border-0' : 'bg-stone-100 text-stone-500 hover:bg-stone-100 shadow-none border-0'}>
                        {p.status === 'ativo' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-stone-400 hover:text-blue-600 hover:bg-blue-50"
                          onClick={() => { setEditing(p); setFormTab("docs"); setDialogOpen(true) }}
                          title="Documentos"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-400 hover:text-stone-600">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEditing(p); setFormTab("geral"); setDialogOpen(true) }}>
                              <Edit className="w-4 h-4 mr-2" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleStatusMutation.mutate({ id: p.id, currentStatus: p.status })}>
                              <Archive className="w-4 h-4 mr-2" /> {p.status === 'ativo' ? 'Inativar' : 'Ativar'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="border-t border-stone-100 bg-stone-50/30">
          <Paginacao
            totalItems={filtrados.length}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
          <DialogHeader className="p-6 pb-2 bg-stone-50 border-b border-stone-100">
            <DialogTitle className="flex items-center gap-2 text-xl">
              {editing ? <Edit className="w-5 h-5 text-indigo-600" /> : <Plus className="w-5 h-5 text-emerald-600" />}
              {editing ? "Editar Parceiro" : "Novo Cadastro"}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados abaixo para {editing ? "atualizar o" : "cadastrar um novo"} parceiro.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6">
            <ParceiroForm parceiro={editing} onSuccess={handleSuccess} onCancel={() => { setDialogOpen(false); setEditing(null) }} initialTab={formTab} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
